/**
 * Navigation config — menu visibility follows backend @RequirePermission codes.
 * Item permission: string = required; string[] = any of (OR).
 * Omit permission = any authenticated user.
 */

import { FEATURE_REPORTS_ENABLED, FEATURE_WAREHOUSE_ENABLED } from './features';

export type NavPermission = string | string[];

export interface NavItem {
  label: string;
  to?: string;
  action?: string;
  sep?: boolean;
  /** Backend permission(s) required to see this link */
  permission?: NavPermission;
}

export interface NavGroup {
  groupLabel: string;
  icon: string;
  items: NavItem[];
}

export interface NavMenu {
  label: string;
  to?: string;
  items?: NavItem[];
  groups?: NavGroup[];
  /** Fallback when child items omit permission */
  permission?: NavPermission;
  /** Hide in phone/tablet drawer; keep on desktop SubNav. */
  desktopOnly?: boolean;
  /** Client cabinet: only the main portal user (no branch) sees this item. */
  portalMainOnly?: boolean;
}

/** РП-портал (показывается rep-only или prepended для пользователей с rep.*) */
export const REP_LEGACY_MENUS: NavMenu[] = [
  { label: 'Сегодня', to: '/representative/dashboard', permission: 'rep.rounds.view' },
  { label: 'Скан рейса', to: '/rep/linehaul', permission: 'rep.rounds.view' },
  { label: 'Остатки', to: '/rep/stock', permission: 'rep.rounds.view' },
  { label: 'Заборы', to: '/rep/pickups', permission: 'rep.rp-rounds.view' },
  { label: 'Водители', to: '/rep/drivers', permission: 'rep.rp-rounds.manage' },
  { label: 'Рейсы доставки', to: '/rep/rp-rounds', permission: 'rep.rp-rounds.view' },
  { label: 'ePOD — до филиала', to: '/rep/pod', permission: 'rep.pod.create' },
  { label: 'Реестр перевозок', to: '/rep/ops', permission: 'rep.rounds.view' },
  { label: 'Перегруз', to: '/rep/xd-outbound', permission: 'rep.rounds.view' },
  {
    label: 'Мой реестр',
    to: '/rep-registry',
    permission: 'rep.rounds.view',
  },
  {
    label: 'Мои расчёты',
    to: '/rep/settlements',
    permission: 'rep.rounds.view',
  },
  { label: 'Сообщения', to: '/messages' },
];

export const DRIVER_PORTAL_MENUS: NavMenu[] = [
  { label: 'Мой рейс', to: '/rep/rp-rounds', permission: 'rep.rp-rounds.view' },
  { label: 'Вручение', to: '/rep/pod', permission: 'rep.pod.create' },
  { label: 'Забор груза', to: '/rep/pickups', permission: 'rep.rp-rounds.view' },
  { label: 'Сообщения', to: '/messages' },
];

export const REP_PORTAL_MENUS: NavMenu[] = [
  { label: 'Сегодня', to: '/representative/dashboard', permission: 'rep.rounds.view' },
  { label: 'Междугородние рейсы', to: '/rep/linehaul', permission: 'rep.rounds.view' },
  { label: 'Перегруз', to: '/rep/xd-outbound', permission: 'rep.rounds.view' },
  { label: 'Склад РП', to: '/rep/stock', permission: 'rep.rounds.view' },
  { label: 'Водители и рейсы', to: '/rep/rp-rounds', permission: 'rep.rp-rounds.view' },
  { label: 'История движений', to: '/rep/ops', permission: 'rep.rounds.view' },
  { label: 'Сообщения', to: '/messages' },
];

/** Портал логиста — только ежедневная работа с заявками, рейсами и транспортом. */
export const LOGISTICS_PORTAL_MENUS: NavMenu[] = [
  { label: 'Рейсы', to: '/all-rounds', permission: ['rounds.view', 'city-rounds.view'] },
  { label: 'Заявки', to: '/all-orders', permission: ['orders.view', 'city-orders.view'] },
  { label: 'Где груз', to: '/cargo-location', permission: 'orders.view' },
  { label: 'Отклонения ТТН', to: '/ttn-deviations', permission: 'orders.status' },
  { label: 'Остатки', to: '/warehouse/stock', permission: ['orders.view', 'city-orders.view'] },
  { label: 'Акты приёмки', to: '/acceptance-reports', permission: 'orders.view' },
  {
    label: 'Транспорт',
    items: [
      { label: 'Перевозчики', to: '/carriers', permission: 'reference.view' },
      { label: 'Водители', to: '/drivers', permission: 'reference.view' },
      { label: 'Транспорт', to: '/transports', permission: 'reference.view' },
      { label: 'Курьеры', to: '/couriers', permission: 'reference.manage' },
      { label: 'Направления', to: '/directions', permission: 'reference.view' },
      { label: 'Сроки доставки', to: '/delivery-times', permission: 'reference.view' },
    ],
  },
  { label: 'Сообщения', to: '/messages' },
];

export function isLogisticsPortalRole(roleName?: string | null): boolean {
  return roleName?.trim().toLocaleLowerCase('ru-RU') === 'логист';
}

/** Склад хаба (погрузка MAIN / приёмка RETURN) — отдельный портал, не РП и не диспетчер. */
export const WAREHOUSE_PORTAL_MENUS: NavMenu[] = [
  { label: 'Городские рейсы', to: '/warehouse/city-rounds', permission: 'rounds.scan' },
  { label: 'Междугородние рейсы', to: '/warehouse/regional-rounds', permission: 'rounds.scan' },
  { label: 'Приёмка', to: '/warehouse/receive', permission: 'rounds.scan' },
  { label: 'Архив', to: '/warehouse/archive', permission: 'rounds.scan' },
  { label: 'Поиск', to: '/warehouse/search', permission: 'rounds.scan' },
  { label: 'Журнал', to: '/warehouse/journal', permission: 'rounds.scan' },
  { label: 'Остатки', to: '/warehouse/stock', permission: ['orders.view', 'city-orders.view'] },
  { label: 'Сообщения', to: '/messages' },
];

/** Клиентский портал — предзаказ и прикрепление бумажной ТТН. */
export const CLIENT_PORTAL_MENUS: NavMenu[] = [
  { label: 'Предзаказы', to: '/client/orders', permission: 'client.ttn.create' },
  { label: 'Учётки', to: '/client/accounts', permission: 'client.ttn.create', portalMainOnly: true },
  { label: 'Сообщения', to: '/messages' },
];

/** Права роли «Склад». Любое лишнее право (orders.view и т.д.) оставляет пользователя в полном TMS. */
const WAREHOUSE_PORTAL_PERMISSIONS = new Set([
  'rounds.scan',
  'rounds.depart',
  'rounds.view',
  'feedback.create',
]);

const CLIENT_PORTAL_PERMISSIONS = new Set([
  'client.ttn.create',
  'client.ttn.view',
  'feedback.create',
]);

/** Основное меню TMS (не rep-only) */
export const NAV_MENUS: NavMenu[] = [
  {
    label: 'Заказы',
    items: [
      { label: 'На контроле', to: '/exceptions', permission: ['orders.view', 'rounds.view'] },
      { label: '📋 Все заявки', to: '/all-orders', permission: ['orders.view', 'city-orders.view'] },
      { label: '📍 Где груз', to: '/cargo-location', permission: 'orders.view' },
      { label: 'Отклонения ТТН', to: '/ttn-deviations', permission: 'orders.status' },
      { label: '🚛 Все рейсы', to: '/all-rounds', permission: ['rounds.view', 'city-rounds.view'] },
      { label: '📦 Рейсы РП (обзор)', to: '/admin/rp-rounds', permission: 'reference.view' },
    ],
  },
  ...(FEATURE_WAREHOUSE_ENABLED
    ? [{
        label: 'Склад',
        items: [
          { label: '📦 Склад (остатки)', to: '/warehouse/stock', permission: ['orders.view', 'city-orders.view'] },
          { label: '📋 Акты приёмки', to: '/acceptance-reports', permission: 'orders.view' },
        ],
      } satisfies NavMenu]
    : []),
  {
    label: 'Редакторы',
    groups: [
      {
        groupLabel: 'Перевозчики и транспорт',
        icon: '🚛',
        items: [
          { label: 'Перевозчики', to: '/carriers', permission: 'reference.view' },
          { label: 'Водители', to: '/drivers', permission: 'reference.view' },
          { label: 'Транспорт', to: '/transports', permission: 'reference.view' },
          { label: 'Курьеры', to: '/couriers', permission: 'reference.manage' },
        ],
      },
      {
        groupLabel: 'Клиенты и партнёры',
        icon: '🤝',
        items: [
          { label: 'Клиенты', to: '/clients', permission: 'reference.view' },
          { label: 'Контактные лица', to: '/contact-faces', permission: 'reference.view' },
          { label: 'Филиалы', to: '/branches', permission: 'reference.view' },
        ],
      },
      {
        groupLabel: 'Региональные представители',
        icon: '🗺️',
        items: [
          { label: 'Исполнители', to: '/regional-representatives', permission: 'reference.view' },
          { label: 'Реестр РП', to: '/rep-registry', permission: 'reference.view' },
        ],
      },
      {
        groupLabel: 'Персонал',
        icon: '👤',
        items: [{ label: 'Персонал', to: '/personals', permission: 'reference.manage' }],
      },
      {
        groupLabel: 'Тарифы и цены',
        icon: '📋',
        items: [
          { label: 'Тарифы', to: '/tarifs', permission: 'reference.view' },
          { label: 'Доп. услуги тарифа', to: '/additional-services', permission: 'reference.view' },
          { label: 'Доп. расходы рейса', to: '/round-extra-services', permission: 'reference.view' },
          { label: 'Направления', to: '/directions', permission: 'reference.view' },
          { label: 'Сроки доставки', to: '/delivery-times', permission: 'reference.view' },
        ],
      },
      {
        groupLabel: 'Справочники',
        icon: '🗂',
        items: [
          { label: 'Города', to: '/cities', permission: 'reference.view' },
          { label: 'Типы коробов', to: '/box-types', permission: 'reference.view' },
          { label: 'Типы транспорта', to: '/transport-types', permission: 'reference.view' },
        ],
      },
      {
        groupLabel: 'Система',
        icon: '⚙️',
        items: [
          { label: 'Фирмы', to: '/firms', permission: 'users.manage' },
          { label: 'Решения', to: '/resolutions', permission: 'reference.manage' },
        ],
      },
    ],
  },
  {
    label: 'Финансы',
    permission: 'finance.view',
    items: [
      { label: 'Платежи', to: '/finance', permission: 'finance.view' },
      { label: 'Дебиторка', to: '/receivables', permission: 'finance.view' },
      { label: 'Реестр перевозок', to: '/delivery-registry', permission: 'finance.view' },
      { label: 'Деньги РП', to: '/rep-settlements', permission: 'finance.view' },
      { label: 'Маржа и утечки', to: '/margin-leaks', permission: 'finance.view' },
      { sep: true, label: '' },
      { label: 'Типы расходов', to: '/cost-types', permission: 'reference.manage' },
      { label: 'Курсы валют', to: '/exchange-rates', permission: 'reference.manage' },
      { label: 'Банки', to: '/banks', permission: 'reference.manage' },
    ],
  },
  {
    label: 'Система',
    items: [
      { label: 'Пользователи', to: '/users', permission: ['users.view', 'users.manage'] },
      { label: 'Роли и права', to: '/roles', permission: 'users.manage' },
      { sep: true, label: '' },
      { label: 'Email очередь', to: '/email-queue', permission: 'users.manage' },
      { label: 'Интеграции', to: '/integrations', permission: 'users.manage' },
      { label: 'Баги тестеров', to: '/tester-feedback', permission: 'feedback.view' },
      { label: 'Журнал событий', to: '/audit-log', permission: 'users.manage' },
    ],
  },
  ...(FEATURE_REPORTS_ENABLED
    ? [{ label: 'Отчётность', to: '/reports', permission: 'finance.view' } satisfies NavMenu]
    : []),
  { label: 'Инциденты', to: '/incidents', permission: 'orders.view' },
  { label: 'Сообщения', to: '/messages' },
];

export function canAccessNavItem(
  permission: NavPermission | undefined,
  check: (code: string) => boolean,
): boolean {
  if (!permission) return true;
  const codes = Array.isArray(permission) ? permission : [permission];
  return codes.some(check);
}

function filterFlatItems(items: NavItem[], check: (code: string) => boolean): NavItem[] {
  return items.filter(item => {
    if (item.sep) return false;
    if (!item.to && !item.action) return false;
    return canAccessNavItem(item.permission, check);
  });
}

/** Filter menus/groups/items by permission; drop empty sections. */
export function filterNavMenus(
  menus: NavMenu[],
  check: (code: string) => boolean,
): NavMenu[] {
  return menus
    .map(menu => {
      if (menu.to && !menu.items && !menu.groups) {
        return canAccessNavItem(menu.permission, check) ? menu : null;
      }

      if (menu.items) {
        const items = filterFlatItems(menu.items, check);
        if (items.length === 0) return null;
        if (menu.permission && !canAccessNavItem(menu.permission, check)) return null;
        return { ...menu, items };
      }

      if (menu.groups) {
        const groups = menu.groups
          .map(group => ({
            ...group,
            items: group.items.filter(
              item => item.to && canAccessNavItem(item.permission, check),
            ),
          }))
          .filter(group => group.items.length > 0);
        if (groups.length === 0) return null;
        if (menu.permission && !canAccessNavItem(menu.permission, check)) return null;
        return { ...menu, groups };
      }

      return null;
    })
    .filter((m): m is NavMenu => m !== null);
}

/** Permission that belongs to the RP portal (plus tester FAB). */
export function isRepPortalPermission(code: string): boolean {
  return code.startsWith('rep.') || code === 'feedback.create';
}

/** True when user has only РП-portal permissions (no TMS back-office). */
export function isRepOnlyPortalUser(permissions: string[]): boolean {
  return permissions.length > 0 && permissions.every(isRepPortalPermission);
}

export function isWarehousePortalPermission(code: string): boolean {
  return WAREHOUSE_PORTAL_PERMISSIONS.has(code);
}

/** True when user is hub-warehouse operator (scan) without dispatcher/admin menu. */
export function isWarehouseOnlyPortalUser(permissions: string[]): boolean {
  return (
    permissions.includes('rounds.scan')
    && permissions.length > 0
    && permissions.every(isWarehousePortalPermission)
  );
}

export function isClientPortalPermission(code: string): boolean {
  return CLIENT_PORTAL_PERMISSIONS.has(code);
}

/** True when user is client-portal only (manual TTN), no TMS back-office. */
export function isClientOnlyPortalUser(permissions: string[]): boolean {
  return (
    permissions.includes('client.ttn.create')
    && permissions.length > 0
    && permissions.every(isClientPortalPermission)
  );
}

/** Post-login / logo home for the current permission set. */
export function resolveHomePath(
  permissions: string[],
  check: (code: string) => boolean = (code) => permissions.includes(code),
  roleName?: string | null,
): string {
  if (isLogisticsPortalRole(roleName)) return '/all-rounds';
  if (isRepOnlyPortalUser(permissions)) return check('rep.rounds.view') ? '/representative/dashboard' : '/rep/rp-rounds';
  if (isWarehouseOnlyPortalUser(permissions)) return '/warehouse/city-rounds';
  if (isClientOnlyPortalUser(permissions)) return '/client/orders';
  if (
    (check('orders.view') || check('city-orders.view'))
    && (check('rounds.view') || check('city-rounds.view'))
  ) {
    return '/exceptions';
  }
  if (check('orders.view') || check('city-orders.view')) return '/all-orders';
  if (check('rounds.view') || check('city-rounds.view')) return '/all-rounds';
  if (check('rounds.scan')) return '/warehouse/city-rounds';
  if (check('client.ttn.create')) return '/client/orders';
  return '/';
}

/** Phone bottom nav (3 destinations + «Ещё»). Icons are resolved in MobileBottomNav. */
export type BottomNavIcon =
  | 'orders'
  | 'rounds'
  | 'warehouse'
  | 'finance'
  | 'messages'
  | 'today'
  | 'pickups'
  | 'stock'
  | 'scan'
  | 'drivers'
  | 'more';

export type BottomNavItem = {
  id: string;
  label: string;
  icon: BottomNavIcon;
  to?: string;
  more?: boolean;
};

/**
 * Up to 3 permission-gated destinations + «Ещё».
 * Does not invent links the user cannot open.
 */
export function buildBottomNavItems(
  permissions: string[],
  check: (code: string) => boolean,
  options?: { representativeId?: string | null; roleName?: string | null },
): BottomNavItem[] {
  const more: BottomNavItem = { id: 'more', label: 'Ещё', icon: 'more', more: true };
  const slots: BottomNavItem[] = [];

  if (isLogisticsPortalRole(options?.roleName)) {
    if (check('rounds.view') || check('city-rounds.view')) {
      slots.push({ id: 'rounds', label: 'Рейсы', to: '/all-rounds', icon: 'rounds' });
    }
    if (check('orders.view') || check('city-orders.view')) {
      slots.push({ id: 'orders', label: 'Заявки', to: '/all-orders', icon: 'orders' });
      slots.push({ id: 'warehouse', label: 'Остатки', to: '/warehouse/stock', icon: 'warehouse' });
    }
    return [...slots.slice(0, 3), more];
  }

  if (isRepOnlyPortalUser(permissions)) {
    if (!check('rep.rounds.view')) {
      if (check('rep.rp-rounds.view')) slots.push({ id: 'driver-round', label: 'Мой рейс', to: '/rep/rp-rounds', icon: 'rounds' });
      if (check('rep.pod.create')) slots.push({ id: 'driver-pod', label: 'Вручение', to: '/rep/pod', icon: 'scan' });
      if (check('rep.rp-rounds.view')) slots.push({ id: 'driver-pickup', label: 'Забор', to: '/rep/pickups', icon: 'pickups' });
      return [...slots, more];
    }
    if (check('rep.rounds.view')) {
      slots.push({ id: 'today', label: 'Сегодня', to: '/representative/dashboard', icon: 'today' });
      slots.push({ id: 'rep-scan', label: 'МГ рейсы', to: '/rep/linehaul', icon: 'scan' });
    }
    if (check('rep.rp-rounds.view') && slots.length < 3) {
      slots.push({ id: 'rep-city', label: check('rep.rounds.view') ? 'Водители' : 'Мой рейс', to: '/rep/rp-rounds', icon: 'rounds' });
    } else if (slots.length < 3) {
      slots.push({ id: 'messages', label: 'Сообщения', to: '/messages', icon: 'messages' });
    }
    const visible = slots.slice(0, 3);
    return [...visible, more];
  }

  if (isWarehouseOnlyPortalUser(permissions)) {
    if (check('rounds.scan')) {
      slots.push({ id: 'wh-city-rounds', label: 'Городские', to: '/warehouse/city-rounds', icon: 'rounds' });
      slots.push({ id: 'wh-regional-rounds', label: 'Междугородние', to: '/warehouse/regional-rounds', icon: 'rounds' });
      slots.push({ id: 'wh-receive', label: 'Приёмка', to: '/warehouse/receive', icon: 'warehouse' });
    }
    return [...slots.slice(0, 3), more];
  }

  if (isClientOnlyPortalUser(permissions)) {
    if (check('client.ttn.create')) {
      slots.push({ id: 'client-ttn', label: 'Заявки', to: '/client/orders', icon: 'orders' });
    }
    slots.push({ id: 'messages', label: 'Сообщения', to: '/messages', icon: 'messages' });
    return [...slots.slice(0, 3), more];
  }

  if (check('orders.view') || check('city-orders.view')) {
    slots.push({ id: 'orders', label: 'Заявки', to: '/all-orders', icon: 'orders' });
  }
  if (check('rounds.view') || check('city-rounds.view')) {
    slots.push({ id: 'rounds', label: 'Рейсы', to: '/all-rounds', icon: 'rounds' });
  }
  if (
    slots.length < 3 &&
    FEATURE_WAREHOUSE_ENABLED &&
    (check('orders.view') || check('city-orders.view'))
  ) {
    slots.push({ id: 'warehouse', label: 'Склад', to: '/warehouse/stock', icon: 'warehouse' });
  }
  if (slots.length < 3 && check('finance.view')) {
    slots.push({ id: 'finance', label: 'Финансы', to: '/finance', icon: 'finance' });
  }
  if (slots.length < 3) {
    slots.push({ id: 'messages', label: 'Сообщения', to: '/messages', icon: 'messages' });
  }
  return [...slots.slice(0, 3), more];
}

export function buildNavMenus(
  permissions: string[],
  check: (code: string) => boolean,
  options?: {
    representativeId?: string | null;
    hideDesktopOnly?: boolean;
    portalMain?: boolean;
    roleName?: string | null;
  },
): NavMenu[] {
  const dropDesktop = (menus: NavMenu[]) =>
    options?.hideDesktopOnly ? menus.filter((m) => !m.desktopOnly) : menus;

  if (isLogisticsPortalRole(options?.roleName)) {
    return filterNavMenus(LOGISTICS_PORTAL_MENUS, check);
  }

  if (isRepOnlyPortalUser(permissions)) {
    return dropDesktop(filterNavMenus(check('rep.rounds.view') ? REP_PORTAL_MENUS : DRIVER_PORTAL_MENUS, check));
  }

  if (isWarehouseOnlyPortalUser(permissions)) {
    return filterNavMenus(WAREHOUSE_PORTAL_MENUS, check);
  }

  if (isClientOnlyPortalUser(permissions)) {
    const menus = filterNavMenus(CLIENT_PORTAL_MENUS, check);
    if (options?.portalMain) return menus;
    return menus.filter((m) => !m.portalMainOnly);
  }

  const main = filterNavMenus(NAV_MENUS, check);
  let menus = main;

  if (check('client.ttn.create') && !isClientOnlyPortalUser(permissions)) {
    menus = [
      { label: 'Предзаказы клиента', to: '/client/orders', permission: 'client.ttn.create' },
      ...menus,
    ];
  }

  if (options?.representativeId) {
    const repLinks = dropDesktop(filterNavMenus(REP_PORTAL_MENUS, check)).filter(
      (m) => m.to !== '/messages',
    );
    if (repLinks.length > 0) {
      menus = [...repLinks, ...menus];
    }
  }

  if (check('rounds.scan')) {
    const hubLinks = filterNavMenus(
      [
        { label: 'Склад хаба', to: '/warehouse/city-rounds', permission: 'rounds.scan' },
        { label: 'Архив склада', to: '/warehouse/archive', permission: 'rounds.scan' },
        { label: 'Поиск склада', to: '/warehouse/search', permission: 'rounds.scan' },
        { label: 'Журнал склада', to: '/warehouse/journal', permission: 'rounds.scan' },
      ],
      check,
    );
    menus = [...hubLinks, ...menus];
  }

  return menus;
}
